import { API_BASE_URL } from '@/config/env';
import type { DevRegAnswers } from '@/lib/devregStorage';
import type { Q7_1Answers } from '@/lib/q7_1Storage';
import type { Q7_2Answers } from '@/lib/q7_2Storage';
import type { Q7_3Answers } from '@/lib/q7_3Storage';
import type { Q7_4Answers } from '@/lib/q7_4Storage';
import { getEffectiveQ7_4Answers } from '@/lib/q7_4Storage';
import { readQ7_1Answers } from '@/lib/q7_1Storage';
import { readQ7_2Answers } from '@/lib/q7_2Storage';
import { readQ7_3Answers } from '@/lib/q7_3Storage';
import { readDevRegAnswers } from '@/lib/devregStorage';
import { readTradeInAnswers } from '@/lib/tradeinStorage';
import type { TradeInAnswers } from '@/lib/tradeinStorage';
import { readSmokerProfileAnswers } from '@/lib/smokerProfileStorage';

function fetchFailureHint(): string {
  if (API_BASE_URL.startsWith('http')) {
    return ` Is the API running at ${API_BASE_URL}?`;
  }
  if (import.meta.env.DEV) {
    return ' Is the Vite dev server running (port 5174) with /api proxied to the backend on port 4000?';
  }
  return ' Check VITE_API_URL in .env.';
}

export type SurveySubmitBody = {
  race: string;
  state: string;
  brand: string;
  variant: string;
  q7_1: Q7_1Answers;
  q7_2: Q7_2Answers;
  q7_3: Q7_3Answers;
  q7_4: Q7_4Answers;
  devreg: DevRegAnswers;
  tradein: TradeInAnswers;
  tradeInContact: {
    name: string;
    email: string;
    mobileLocalDigits: string;
  };
};

export type SurveySubmitTngResponse = {
  pin: string;
  expirydate: string | null;
  value: string | null;
  reused: boolean;
};

/** Ensures JSON has explicit `null`s (JSON.stringify drops `undefined`, which breaks Zod). */
export function serializeSurveySubmitBody(body: SurveySubmitBody): SurveySubmitBody {
  const q = body.q7_4;
  const q71 = body.q7_1.selectedOutlets;
  const q72 = body.q7_2.selected;
  return {
    ...body,
    q7_1: {
      selectedOutlets: q71.length > 1 ? [q71[0]] : q71,
    },
    q7_2: {
      selected: q72.length > 1 ? [q72[0]] : q72,
    },
    q7_4: {
      answer: q.answer.trim() || '(no text)',
      cantFindLocation: q.cantFindLocation,
      outletUuid: q.outletUuid ?? null,
      address: q.address ?? '',
      outletName: q.outletName ?? null,
    },
  };
}

/**
 * POST /api/v1/customers/:customerUuid/survey-submit — persist full survey to Customer.
 * For `yes_help`, API also returns TNG fields for the voucher (same shape as survey-submit-tng).
 */
export async function submitCustomerSurvey(
  customerUuid: string,
  body: SurveySubmitBody
): Promise<SurveySubmitTngResponse | null> {
  const payload = serializeSurveySubmitBody(body);
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api/v1/customers/${customerUuid}/survey-submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    throw new Error(
      e instanceof Error && e.message === 'Failed to fetch'
        ? `Could not reach the server.${fetchFailureHint()}`
        : e instanceof Error
          ? e.message
          : 'Network error'
    );
  }
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
    details?: unknown;
    pin?: string;
    expirydate?: string | null;
    value?: string | null;
    reused?: boolean;
  };
  if (!res.ok) {
    const msg =
      typeof data.error === 'string'
        ? data.error
        : `Save failed (${res.status}).`;
    const flat = data.details as { fieldErrors?: Record<string, unknown> } | undefined;
    const fe = flat?.fieldErrors;
    const hint =
      fe != null && Object.keys(fe).length > 0
        ? ` (${Object.entries(fe)
            .map(([k, v]) => `${k}: ${String(v)}`)
            .join('; ')})`
        : '';
    throw new Error(msg + hint);
  }
  if (data.ok !== true) {
    throw new Error('Unable to save survey');
  }
  if (typeof data.pin === 'string') {
    return {
      pin: data.pin,
      expirydate: data.expirydate ?? null,
      value: data.value ?? null,
      reused: data.reused === true,
    };
  }
  return null;
}

/** Builds the survey POST body from localStorage (same shape as trade-in contact submit). */
export function buildSurveySubmitBodyFromStorage(
  tradeInContact: SurveySubmitBody['tradeInContact']
): SurveySubmitBody | null {
  const smoker = readSmokerProfileAnswers();
  const q7_1 = readQ7_1Answers();
  const q7_2 = readQ7_2Answers();
  const q7_3 = readQ7_3Answers();
  const q7_4 = getEffectiveQ7_4Answers();
  const devreg = readDevRegAnswers();
  const tradein = readTradeInAnswers();

  if (
    !smoker?.race?.trim() ||
    !smoker?.state?.trim() ||
    !smoker?.brand?.trim() ||
    !smoker?.variant?.trim() ||
    !q7_1 ||
    !q7_2 ||
    !q7_3 ||
    !q7_4 ||
    !devreg ||
    !tradein
  ) {
    return null;
  }

  return {
    race: smoker.race,
    state: smoker.state,
    brand: smoker.brand,
    variant: smoker.variant,
    q7_1,
    q7_2,
    q7_3,
    q7_4,
    devreg,
    tradein,
    tradeInContact,
  };
}

/**
 * POST /api/v1/customers/:customerUuid/survey-submit-tng — full survey + assign TNG PIN.
 */
export async function submitCustomerSurveyTng(
  customerUuid: string,
  body: SurveySubmitBody
): Promise<SurveySubmitTngResponse> {
  const payload = serializeSurveySubmitBody(body);
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api/v1/customers/${customerUuid}/survey-submit-tng`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    throw new Error(
      e instanceof Error && e.message === 'Failed to fetch'
        ? `Could not reach the server.${fetchFailureHint()}`
        : e instanceof Error
          ? e.message
          : 'Network error'
    );
  }
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
    details?: unknown;
    pin?: string;
    expirydate?: string | null;
    value?: string | null;
    reused?: boolean;
  };
  if (!res.ok) {
    const msg =
      typeof data.error === 'string'
        ? data.error
        : `Save failed (${res.status}).`;
    const flat = data.details as { fieldErrors?: Record<string, unknown> } | undefined;
    const fe = flat?.fieldErrors;
    const hint =
      fe != null && Object.keys(fe).length > 0
        ? ` (${Object.entries(fe)
            .map(([k, v]) => `${k}: ${String(v)}`)
            .join('; ')})`
        : '';
    throw new Error(msg + hint);
  }
  if (data.ok !== true || typeof data.pin !== 'string') {
    throw new Error('Unable to save survey or assign PIN');
  }
  return {
    pin: data.pin,
    expirydate: data.expirydate ?? null,
    value: data.value ?? null,
    reused: data.reused === true,
  };
}
